import React from "react"
import styled from "styled-components"
import Img from "gatsby-image"
import { graphql } from "gatsby"
import ButtonLink from "../../components/ButtonLink"
import Card from "../../components/Card"
import ActionCard from "../../components/ActionCard"
import CalloutBanner from "../../components/CalloutBanner"
import Emoji from "../../components/Emoji"
import ExpandableCard from "../../components/ExpandableCard"
import GhostCard from "../../components/GhostCard"
import Link from "../../components/Link"
import PageMetadata from "../../components/PageMetadata"
import PageHero from "../../components/PageHero"
import Breadcrumbs from "../../components/Breadcrumbs"
import {
  CardContainer,
  Content,
  Page,
  Divider,
} from "../../components/SharedStyledComponents"

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  @media (max-width: ${(props) => props.theme.breakpoints.l}) {
    flex-direction: column;
  }
`

const H2 = styled.h2`
  font-size: 28px;
  font-weight: 700;
  margin-top: 0;
  margin-bottom: 1.5rem;
`

const CentreCard = styled(Card)`
  flex: 1 1 30%;
  min-width: 240px;
  margin: 1rem;
  padding: 1.5rem;
  box-shadow: ${(props) => props.theme.colors.tableBoxShadow};
  border: 1px solid ${(props) => props.theme.colors.lightBorder};
  border-radius: 8px;
  @media (max-width: ${(props) => props.theme.breakpoints.l}) {
    flex: 1 1 30%;
  }
`

const StyledCardContainer = styled(CardContainer)`
  margin-top: 2rem;
  margin-bottom: 3rem;
`

const GridContainer = styled.div`
  margin-top: 4rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
`

const Vision = styled.div`
  margin-top: 3rem;
`

const StyledCallout = styled(CalloutBanner)`
  margin: 4rem 0;
  .gatsby-image-wrapper {
    border-radius: 12px;
  }
`

const AIPage = ({ data, location }) => {
  const heroContent = {
    title: "AI FACTORY",
    header:
      "Soluciones de Inteligencia Artificial para el Siguiente Nivel Corporativo",
    subtitle:
      "Diseñamos, entrenamos e implementamos asistentes virtuales avanzados, agentes autónomos y flujos automatizados utilizando Model Context Protocol (MCP) e integraciones con LLMs líderes.",
    image: data.hero.childImageSharp.fluid,
    alt: "Newinntech AI Solutions",
    buttons: [
      {
        path: "/productos/api-monitor/",
        content: "Explorar API Monitor AI",
      },
      {
        path: "/productos/gencode-ai/",
        content: "Gencode AI",
        isSecondary: true,
      },
    ],
  }

  const pillars = [
    {
      emoji: ":robot:",
      title: "Agentes Cognitivos Autónomos",
      description:
        "Sistemas inteligentes que no solo responden preguntas, sino que planifican, interactúan con APIs externas y ejecutan tareas operativas complejas de forma autónoma.",
    },
    {
      emoji: ":electric_plug:",
      title: "Model Context Protocol (MCP)",
      description:
        "Garantizamos la máxima seguridad corporativa conectando modelos de IA directamente con tus fuentes de datos locales y APIs seguras, aislando la información sensible.",
    },
    {
      emoji: ":brain:",
      title: "Procesamiento NLP y Modelado",
      description:
        "Análisis de lenguaje natural, clasificación inteligente de documentos, minería de texto y optimización de bases de conocimiento vectoriales (RAG) a medida.",
    },
  ]

  const productFeatures = [
    {
      title: "Process AI",
      description:
        "Nuestra suite de automatización inteligente orientada a automatizar workflows operativos empresariales usando procesamiento natural de lenguaje.",
      to: "https://process-ai.newinntech.com",
    },
    {
      title: "Gencode AI",
      description:
        "Acelera tus desarrollos de software con generación e inspección de código automatizado que sigue los más estrictos estándares de calidad del equipo.",
      to: "/productos/gencode-ai/",
    },
    {
      title: "DevSecOps AI Integrado",
      description:
        "Diagnosis avanzada de errores en pipelines de construcción e infraestructura cloud interactuando con un chatbot experto en DevSecOps.",
      to: "/productos/devsecops-monitor-ai/",
    },
  ]

  return (
    <Page>
      <PageMetadata
        title="Fábrica de Inteligencia Artificial (AI Factory)"
        description="Explora las soluciones de Inteligencia Artificial de New Inntech S.A.S. Agentes autónomos, integraciones con MCP y procesamiento cognitivo."
      />
      <PageHero content={heroContent} />
      <Divider />

      <Content>
        <Breadcrumbs slug={location.pathname} />

        <Vision>
          <H2>
            Nuestra Visión de la Inteligencia Artificial{" "}
            <Emoji ml={`0.5rem`} text=":sparkles:" />
          </H2>
          <p>
            En Newinntech, entendemos la IA como una extensión colaborativa del
            potencial humano y organizativo. No construimos simples chatbots;
            diseñamos infraestructuras cognitivas integradas que impulsan la
            eficiencia real, resuelven cuellos de botella y protegen celosamente
            los datos de tu corporación.
          </p>

          <StyledCardContainer>
            {pillars.map((pillar, idx) => (
              <CentreCard
                key={idx}
                emoji={pillar.emoji}
                title={pillar.title}
                description={pillar.description}
              />
            ))}
          </StyledCardContainer>
        </Vision>

        <Divider />

        <H2>Ecosistema de Productos de Inteligencia Artificial</H2>
        <p>
          Descubre las herramientas nativas diseñadas por nuestro equipo de
          ingenieros para resolver problemas empresariales específicos:
        </p>

        <GridContainer>
          {productFeatures.map((product, idx) => (
            <GhostCard key={idx}>
              <h3>{product.title}</h3>
              <p>{product.description}</p>
              {product.to.startsWith("http") ? (
                <Link to={product.to}>Ir a la aplicación externa</Link>
              ) : (
                <Link to={product.to}>Ver ficha de producto</Link>
              )}
            </GhostCard>
          ))}
        </GridContainer>

        <Divider />

        <StyledCallout
          image={data.callout.childImageSharp.fluid}
          alt="AI Solutions Callout"
          title="¿Listo para empoderar tus operaciones con Inteligencia Artificial?"
          description="Escríbenos y trabajemos juntos en el diseño, desarrollo e implementación de asistentes y flujos inteligentes que transformen tus procesos organizativos."
        >
          <div>
            <ButtonLink to="https://www.newinntech.com/">
              Contactar Especialista
            </ButtonLink>
          </div>
        </StyledCallout>
      </Content>
    </Page>
  )
}

export default AIPage

export const query = graphql`
  query {
    hero: file(relativePath: { eq: "ai-dev3.png" }) {
      childImageSharp {
        fluid(maxWidth: 800) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    callout: file(relativePath: { eq: "ai_banner10.png" }) {
      childImageSharp {
        fluid(maxWidth: 800) {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`
